import { Modal } from '@components/core/modal.component';
import { Button } from '@components/ui/button';
import { useAppForm } from '@components/ui/form/form';
import { errorToast, successToast } from '@hooks/toast.hook';
import { useAdminAvailableParentDomainsQuery, useAdminCreateDomain } from '@queries/admin-domains.query';
import { domainSchema } from '@schemas/domain.schema';
import { createFormInvalidHandler } from '@utils/form.util';
import { mapToSelectOptions } from '@utils/select.util';
import { Loader2Icon } from 'lucide-react';
import { parseAsString, useQueryState } from 'nuqs';
import React from 'react';
import { adminDomainFormOptions } from './admin-domain-form-options';

type AdminDomainCreateModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

export const AdminDomainCreateModal: React.FC<AdminDomainCreateModalProps> = (props) => {
  const { isOpen, onClose } = props;
  const [, setSelectedDomainId] = useQueryState('domainId', parseAsString);
  const { mutateAsync: createDomain, isPending } = useAdminCreateDomain();
  const { data: parentDomainsData } = useAdminAvailableParentDomainsQuery();

  const form = useAppForm({
    ...adminDomainFormOptions,

    validators: {
      onSubmit: domainSchema,
    },

    onSubmit: async ({ value }) => {
      await createDomain(
        {
          name: value.name ?? '',
          parentDomainId: value.parentDomainId ?? undefined,
        },
        {
          onSuccess: (data) => {
            successToast({ description: 'Domain created successfully' });
            void setSelectedDomainId(data.id);
            handleClose();
          },
          onError: (error) => errorToast({ description: error.message || 'Failed to create domain' }),
        },
      );
    },
    onSubmitInvalid: createFormInvalidHandler(),
  });

  const handleClose = () => {
    form.reset();
    onClose();
  };

  const parentOptions = mapToSelectOptions(parentDomainsData?.domains ?? []);

  if (!isOpen) return null;

  return (
    <Modal
      centered
      isOpen={isOpen}
      onClose={handleClose}
      header={{
        className: 'text-left',
        title: 'Create Domain',
      }}
      footer={{
        rightButton: (
          <div className="flex gap-2 justify-end">
            <Button
              disabled={isPending}
              onClick={handleClose}
              variant="secondary"
            >
              Cancel
            </Button>
            <Button
              disabled={isPending}
              onClick={() => form.handleSubmit()}
              type="button"
            >
              {isPending && <Loader2Icon className="size-3 mr-1 animate-spin" />}
              Save
            </Button>
          </div>
        ),
      }}
      width="640px"
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          event.stopPropagation();
          void form.handleSubmit();
        }}
      >
        <form.AppForm>
          <div className="flex flex-col gap-4">
            <form.AppField name="name">
              {(field) => (
                <field.TextField
                  autoComplete="off"
                  data-1p-ignore
                  label="Name"
                  placeholder="Enter a domain name"
                />
              )}
            </form.AppField>

            <form.AppField name="parentDomainId">
              {(field) => (
                <field.SelectPopoverField
                  label="Parent Domain"
                  onClear={() => field.handleChange(null)}
                  options={parentOptions}
                  placeholder="Select a parent domain"
                />
              )}
            </form.AppField>
          </div>
        </form.AppForm>
      </form>
    </Modal>
  );
};
